import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, AlertCircle, Edit2, Upload } from 'lucide-react';
import axios from 'axios';
import { COUNTRIES_DATA, COUNTRIES, PROFESSIONS } from '../data/locations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RetrieveProfile = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [formData, setFormData] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [portfolioFile, setPortfolioFile] = useState(null);

  const states = formData?.country ? COUNTRIES_DATA[formData.country] || [] : [];

  const handleSearch = async () => {
    setError('');
    setProfile(null);
    setLoading(true);
    try {
      // Try professional profile first
      const response = await axios.get(`${API_URL}/api/profiles/retrieve`, {
        params: { search: searchTerm.trim() }
      });
      setProfile({ ...response.data, _type: 'professional' });
      setFormData({ ...response.data });
    } catch (err) {
      // Try professional request
      try {
        const reqRes = await axios.get(`${API_URL}/api/professional-requests/retrieve`, {
          params: { search: searchTerm.trim() }
        });
        setProfile({ ...reqRes.data, _type: 'requester' });
        setFormData(null);
      } catch {
        setError('No profile found. Please check your ID or email and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'country') {
      setFormData(prev => ({ ...prev, country: value, state: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setUpdateSuccess(false);

    try {
      // Try two-step approach first
      try {
        await axios.post(`${API_URL}/api/profiles/submit-info`, {
          unique_id: profile.unique_id,
          full_name: formData.full_name,
          phone: formData.phone || '',
          email: formData.email,
          date_of_birth: formData.date_of_birth || '',
          age: formData.age || '',
          profession: formData.profession,
          other_profession: formData.other_profession || '',
          experience: formData.experience,
          country: formData.country || '',
          state: formData.state || '',
          city: '',
          address: formData.address || '',
          gender: formData.gender || '',
          skills: formData.skills || '',
          summary: formData.summary || '',
        });

        if (cvFile) {
          const cvData = new FormData();
          cvData.append('cv_file', cvFile);
          await axios.post(`${API_URL}/api/profiles/upload-cv/${profile.unique_id}`, cvData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
          });
        }
        if (photoFile) {
          const photoData = new FormData();
          photoData.append('photo', photoFile);
          await axios.post(`${API_URL}/api/profiles/upload-photo/${profile.unique_id}`, photoData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
          });
        }
        if (portfolioFile) {
          const portData = new FormData();
          portData.append('portfolio', portfolioFile);
          await axios.post(`${API_URL}/api/profiles/upload-portfolio/${profile.unique_id}`, portData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
          });
        }
      } catch (twoStepErr) {
        // Fallback: single multipart request
        const data = new FormData();
        data.append('unique_id', profile.unique_id);
        data.append('full_name', formData.full_name);
        data.append('phone', formData.phone || '');
        data.append('email', formData.email);
        data.append('date_of_birth', formData.date_of_birth || '');
        data.append('age', formData.age || '');
        data.append('profession', formData.profession);
        data.append('other_profession', formData.other_profession || '');
        data.append('experience', formData.experience);
        data.append('country', formData.country || '');
        data.append('state', formData.state || '');
        data.append('city', '');
        data.append('address', formData.address || '');
        data.append('gender', formData.gender || '');
        data.append('skills', formData.skills || '');
        data.append('summary', formData.summary || '');
        if (cvFile) {
          data.append('cv_file', cvFile);
        } else if (profile.cv_path) {
          const resp = await fetch(`${API_URL}/api/files/${profile.cv_path}`);
          const blob = await resp.blob();
          data.append('cv_file', new File([blob], 'cv.pdf', { type: 'application/pdf' }));
        }
        if (photoFile) {
          data.append('passport_photo', photoFile);
        }
        if (portfolioFile) {
          data.append('portfolio_file', portfolioFile);
        }
        await axios.post(`${API_URL}/api/profiles/submit`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
      }

      setUpdateSuccess(true);
      setEditing(false);
      setCvFile(null);
      setPhotoFile(null);
      setPortfolioFile(null);
      const refreshed = await axios.get(`${API_URL}/api/profiles/retrieve`, {
        params: { search: profile.unique_id }
      });
      setProfile(refreshed.data);
      setFormData(refreshed.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      } else if (err.code === 'ERR_NETWORK') {
        setError('Update failed. Please try again.');
      } else {
        setError(err.message || 'Update failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-slate-900/40 border border-slate-700 text-slate-100 h-11 px-4 text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 rounded transition-colors";
  const labelClass = "text-xs uppercase tracking-widest text-slate-400 font-semibold mb-1.5 block";

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-slate-200">
      <header className="sticky top-0 z-50 bg-[#0B0F1A]/90 backdrop-blur-xl border-b border-slate-800/60 h-16 flex items-center justify-center px-6">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <img 
            src="https://customer-assets.emergentagent.com/job_submission-hub-21/artifacts/1pp9pqr0_1000260687.png" 
            alt="BuildDirect Logo" 
            className="h-10"
          />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {!profile ? (
          <div className="space-y-6">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl tracking-tight mb-2 text-slate-100">Retrieve or Update Your Profile</h1>
              <p className="text-slate-400 text-sm">For professionals, project owners, contractors, and organizations.</p>
              <p className="text-slate-500 text-xs mt-1">Use your unique ID (BDO-... or BDR-...) or your email address to access your profile.</p>
            </div>

            {error && (
              <div className="bg-red-900/20 border border-red-500/40 p-3 flex items-start gap-2 rounded" data-testid="error-message">
                <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            <div className="bg-[#0E1525] border border-slate-800 p-4 sm:p-6 rounded">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Enter email or unique ID"
                  data-testid="search-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full sm:flex-1 bg-slate-900/40 border border-slate-700 text-slate-100 h-11 px-4 text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 rounded transition-colors"
                />
                <button
                  onClick={handleSearch}
                  disabled={loading || !searchTerm}
                  data-testid="search-button"
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white px-6 h-11 text-xs uppercase tracking-wider font-semibold transition-colors rounded flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <Search className="h-4 w-4" />
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>
          </div>
        ) : profile._type === 'requester' ? (
          /* Requester Profile Display */
          <div className="space-y-6">
            <div>
              <h1 className="font-heading text-2xl tracking-tight mb-1 text-slate-100">Your Request Profile</h1>
              <p className="text-xs text-slate-500 font-mono">{profile.unique_id}</p>
            </div>

            <div className="bg-[#0E1525] border border-slate-800 p-6 rounded space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { label: 'Name / Company', value: profile.full_name },
                  { label: 'Email', value: profile.email },
                  { label: 'Phone', value: profile.phone },
                  { label: 'Country', value: profile.country },
                  { label: 'State / City', value: profile.state },
                  { label: 'Purpose', value: profile.purpose === 'project' ? 'Project-based' : 'Organization-based' },
                  { label: 'Project Type', value: profile.project_type },
                  { label: 'Timeline', value: profile.timeline },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">{label}</div>
                    <div className="text-sm text-slate-200">{value || 'N/A'}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Professionals Requested</div>
                <div className="flex flex-wrap gap-1.5">
                  {(profile.needs || []).map((n, i) => (
                    <span key={i} className="text-xs bg-blue-900/30 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded font-semibold">{n}</span>
                  ))}
                </div>
              </div>
              {profile.description && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Description</div>
                  <div className="text-sm text-slate-200">{profile.description}</div>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Status</div>
                <div className="text-sm text-slate-200 capitalize">{profile.status}</div>
              </div>
            </div>

            <button
              onClick={() => { setProfile(null); setSearchTerm(''); }}
              data-testid="back-to-search-button"
              className="text-slate-400 hover:text-slate-200 text-xs uppercase tracking-wider font-semibold transition-colors"
            >
              &larr; Back to Search
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-heading text-2xl tracking-tight mb-1 text-slate-100">Your Profile</h1>
                <p className="text-xs text-slate-500 font-mono">{profile.unique_id}</p>
              </div>
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  data-testid="edit-profile-button"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 text-xs uppercase tracking-wider font-semibold transition-colors rounded flex items-center gap-2"
                >
                  <Edit2 className="h-3.5 w-3.5" />
                  Edit
                </button>
              )}
            </div>

            {updateSuccess && (
              <div className="bg-green-900/20 border border-green-500/40 p-3 rounded" data-testid="success-message">
                <p className="text-green-300 text-sm">Profile updated successfully!</p>
              </div>
            )}

            {error && (
              <div className="bg-red-900/20 border border-red-500/40 p-3 flex items-start gap-2 rounded" data-testid="error-message">
                <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            {editing ? (
              <form onSubmit={handleUpdate} className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-xs uppercase tracking-widest text-slate-400 font-semibold border-b border-slate-800 pb-2">Personal</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Full Name</label>
                      <input type="text" name="full_name" value={formData.full_name} onChange={handleChange}
                        data-testid="edit-full-name" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Phone</label>
                      <input type="tel" name="phone" value={formData.phone || ''} onChange={handleChange}
                        data-testid="edit-phone" className={inputClass} />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Gender</label>
                    <select name="gender" value={formData.gender || ''} onChange={handleChange}
                      data-testid="edit-gender" className={inputClass}>
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Date of Birth</label>
                      <input type="date" name="date_of_birth" value={formData.date_of_birth || ''} onChange={handleChange}
                        data-testid="edit-dob" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Age</label>
                      <input type="number" name="age" min="16" max="80" value={formData.age || ''} onChange={handleChange}
                        placeholder="e.g. 32" data-testid="edit-age" className={inputClass} />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xs uppercase tracking-widest text-slate-400 font-semibold border-b border-slate-800 pb-2">Location</h3>
                  <div>
                    <label className={labelClass}>Country</label>
                    <select name="country" value={formData.country || ''} onChange={handleChange}
                      data-testid="edit-country" className={inputClass}>
                      <option value="">Select Country</option>
                      {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>State / Region</label>
                    <select name="state" value={formData.state || ''} onChange={handleChange}
                      data-testid="edit-state" className={inputClass}>
                      <option value="">{formData.country ? 'Select' : 'Select country first'}</option>
                      {states.map(s => <option key={s} value={s}>{s}</option>)}
                      {formData.state && !states.includes(formData.state) && (
                        <option value={formData.state}>{formData.state}</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Home Address</label>
                    <input type="text" name="address" value={formData.address || ''} onChange={handleChange}
                      placeholder="Enter your full home address" data-testid="edit-address" className={inputClass} />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xs uppercase tracking-widest text-slate-400 font-semibold border-b border-slate-800 pb-2">Professional</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Profession</label>
                      <select name="profession" value={formData.profession} onChange={handleChange}
                        data-testid="edit-profession" className={inputClass}>
                        {PROFESSIONS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Experience</label>
                      <input type="text" name="experience" value={formData.experience} onChange={handleChange}
                        data-testid="edit-experience" className={inputClass} />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Skills</label>
                    <input type="text" name="skills" value={formData.skills || ''} onChange={handleChange}
                      data-testid="edit-skills" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Summary</label>
                    <textarea name="summary" value={formData.summary || ''} onChange={handleChange} rows={2}
                      data-testid="edit-summary"
                      className="w-full bg-slate-900/40 border border-slate-700 text-slate-100 px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 rounded transition-colors" />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xs uppercase tracking-widest text-slate-400 font-semibold border-b border-slate-800 pb-2">Upload / Re-upload Documents</h3>
                  <div>
                    <label className={labelClass}>Passport Photograph</label>
                    <div className="border border-dashed border-slate-700 hover:border-blue-500/50 rounded p-4 text-center bg-slate-900/30 transition-colors">
                      <Upload className="h-5 w-5 text-slate-500 mx-auto mb-1" />
                      <input type="file" accept="image/*"
                        onChange={(e) => e.target.files[0] && setPhotoFile(e.target.files[0])}
                        data-testid="edit-photo-file" className="text-sm text-slate-400 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700" />
                      {photoFile && <p className="text-xs text-green-400 mt-1">{photoFile.name}</p>}
                      {!photoFile && profile.passport_photo_path && <p className="text-xs text-slate-500 mt-1">Current photo on file</p>}
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>CV (PDF)</label>
                    <div className="border border-dashed border-slate-700 hover:border-blue-500/50 rounded p-4 text-center bg-slate-900/30 transition-colors">
                      <Upload className="h-5 w-5 text-slate-500 mx-auto mb-1" />
                      <input type="file" accept=".pdf"
                        onChange={(e) => e.target.files[0] && setCvFile(e.target.files[0])}
                        data-testid="edit-cv-file" className="text-sm text-slate-400 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700" />
                      {cvFile && <p className="text-xs text-green-400 mt-1">{cvFile.name}</p>}
                      {!cvFile && profile.cv_path && <p className="text-xs text-slate-500 mt-1">Current CV on file</p>}
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Portfolio (PDF or Images)</label>
                    <div className="border border-dashed border-slate-700 hover:border-blue-500/50 rounded p-4 text-center bg-slate-900/30 transition-colors">
                      <Upload className="h-5 w-5 text-slate-500 mx-auto mb-1" />
                      <input type="file" accept=".pdf,image/*"
                        onChange={(e) => e.target.files[0] && setPortfolioFile(e.target.files[0])}
                        data-testid="edit-portfolio-file" className="text-sm text-slate-400 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700" />
                      {portfolioFile && <p className="text-xs text-green-400 mt-1">{portfolioFile.name}</p>}
                      {!portfolioFile && profile.portfolio_path && <p className="text-xs text-slate-500 mt-1">Current portfolio on file</p>}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button type="submit" disabled={loading} data-testid="save-changes-button"
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white px-6 py-2.5 text-xs uppercase tracking-wider font-semibold transition-colors rounded shadow-lg shadow-blue-600/20 hover:shadow-blue-600/40">
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button type="button" data-testid="cancel-edit-button"
                    onClick={() => { setEditing(false); setFormData(profile); setCvFile(null); setPhotoFile(null); setPortfolioFile(null); }}
                    className="border border-slate-700 hover:border-slate-500 hover:bg-slate-800/40 text-slate-200 px-6 py-2.5 text-xs uppercase tracking-wider font-semibold transition-colors rounded">
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="bg-[#0E1525] border border-slate-800 p-6 rounded space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { label: 'Full Name', value: profile.full_name },
                    { label: 'Email', value: profile.email },
                    { label: 'Phone', value: profile.phone },
                    { label: 'Gender', value: profile.gender },
                    { label: 'Date of Birth', value: profile.date_of_birth },
                    { label: 'Age', value: profile.age },
                    { label: 'Country', value: profile.country },
                    { label: 'State / Region', value: profile.state },
                    { label: 'Address', value: profile.address },
                    { label: 'Profession', value: profile.profession },
                    { label: 'Experience', value: profile.experience },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">{label}</div>
                      <div className="text-sm text-slate-200" data-testid={`display-${label.toLowerCase().replace(/[\s\/]+/g, '-')}`}>{value || 'N/A'}</div>
                    </div>
                  ))}
                </div>
                {profile.skills && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Skills</div>
                    <div className="text-sm text-slate-200">{profile.skills}</div>
                  </div>
                )}
                {profile.summary && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Summary</div>
                    <div className="text-sm text-slate-200">{profile.summary}</div>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => { setProfile(null); setSearchTerm(''); setEditing(false); }}
              data-testid="back-to-search-button"
              className="text-slate-400 hover:text-slate-200 text-xs uppercase tracking-wider font-semibold transition-colors"
            >
              &larr; Back to Search
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RetrieveProfile;
